import type { EnumeratedEnumVFrame } from '@festina/life-general-types/life'
import {
  amountUtils,
  dateUtils,
  type Except,
  type SetOptional,
  translationUtils,
} from '@festina-life/core'
import {
  FormikRadioGroup,
  type FormikRadioProps,
} from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import get from 'lodash-es/get'

import { isFieldRequired } from '../../forms/formUtility'
import { useLifeFormikContext } from '../../forms/useLifeFormikContext'
import {
  isBooleanVFrame,
  isDateVFrame,
  isDontUseVFrame,
  isEnumeratedVFrame,
  isFixedVFrame,
  isFractionVFrame,
} from '../../forms/yup/validation/types'
// add to index file, type index
import { isVFrameWithDefaultValue } from '../../forms/yup/validation/yupValidationUtils'
import { useFieldVFrame } from '../useFieldVFrame'

type LifeRadioGroupProps<T> = SetOptional<
  Except<FormikRadioProps<T>, 'isEditable' | 'value'>,
  'options'
>

export const LifeRadioGroup = <T,>({
  name,
  options,
  ...restProps
}: LifeRadioGroupProps<T>) => {
  const translate = useTranslate()
  const { values, setFieldValue } = useLifeFormikContext()
  const frameStatus = useFieldVFrame(name)

  if (isDontUseVFrame(frameStatus)) {
    return null
  }

  const value = get(values, name as string) as T
  const isFixed = isFixedVFrame(frameStatus)
  const isDefaultValue = isVFrameWithDefaultValue(frameStatus)
  const isRequired = isFieldRequired(frameStatus)

  const getValue = () => {
    if (isFixed) {
      return frameStatus.value as T
    }

    if (
      isDefaultValue &&
      !value &&
      typeof frameStatus.defaultValue !== 'boolean'
    ) {
      return frameStatus.defaultValue as T
    }

    return value
  }

  let frameOptions: T[] = []
  let renderFn = restProps.renderFn

  if (isEnumeratedVFrame(frameStatus)) {
    frameOptions = [...(frameStatus.values ?? [])] as T[]

    if (isBooleanVFrame(frameStatus)) {
      renderFn = (x: T) => translationUtils.translateBoolean(!!x, translate)
    } else if (isFractionVFrame(frameStatus)) {
      renderFn = (x: T) => amountUtils.formatPercentWithDecimals(x as number)
    } else if (isDateVFrame(frameStatus)) {
      renderFn = (x: T) => dateUtils.dateAsStr(x as Date)
    }
  } else if (isBooleanVFrame(frameStatus)) {
    frameOptions = [true, false] as T[]
    renderFn = (x: T) => translationUtils.translateBoolean(!!x, translate)
  }

  const defaultValue = isVFrameWithDefaultValue(frameStatus)
    ? ((frameStatus as EnumeratedEnumVFrame)?.defaultValue as T)
    : undefined

  return (
    <FormikRadioGroup
      name={name}
      isEditable={!isFixed}
      value={getValue()}
      defaultValue={defaultValue}
      onDefaultValueClick={() => setFieldValue(name, defaultValue)}
      isRequired={isRequired}
      renderFn={renderFn}
      options={options ?? frameOptions}
      hasRightColumnAlways
      {...restProps}
    />
  )
}
